
import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { 
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarFooter,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { 
  Users, 
  Settings, 
  LayoutDashboard, 
  LogOut, 
  Home,
  FileText,
  Image,
  BarChart3,
  Trophy,
  Layout,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  Bell
} from 'lucide-react';

interface AdminLayoutProps {
  children: ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userRole, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      // Clear user state and force navigation to auth page
      window.location.href = '/auth';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if signOut fails, force redirect to auth page
      window.location.href = '/auth';
    }
  };

  // Toggle button for collapsing/expanding the sidebar with clear arrow icon
  const SidebarToggleButton = () => {
    const { state, toggleSidebar } = useSidebar();
    const isCollapsed = state === 'collapsed';
    return (
      <Button
        variant="ghost"
        size="icon"
        className="ml-1 h-7 w-7"
        aria-label={isCollapsed ? 'Sidebar einblenden' : 'Sidebar einklappen'}
        onClick={toggleSidebar}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </Button>
    );
  };

  const menuItems = [
    {
      path: '/admin',
      label: 'Dashboard',
      icon: LayoutDashboard,
    },
    {
      path: '/admin/training',
      label: 'Training',
      icon: Users,
    },
    {
      path: '/admin/blog',
      label: 'Blog',
      icon: FileText,
    },
    {
      path: '/admin/events',
      label: 'Events',
      icon: Layout,
    },
    {
      path: '/admin/media',
      label: 'Medien',
      icon: Image,
    },
    {
      path: '/admin/tournaments',
      label: 'Turniere',
      icon: Trophy,
    },
    {
      path: '/admin/files',
      label: 'Dateien',
      icon: FolderOpen,
    },
    {
      path: '/admin/banner',
      label: 'Banner',
      icon: Bell,
    },
    {
      path: '/admin/analytics',
      label: 'Traffic Analytics',
      icon: BarChart3,
    },
    {
      path: '/admin/settings',
      label: 'Einstellungen',
      icon: Settings,
    },
  ];

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20">
        {/* Global Header with toggle */}
          <header className="sticky top-0 z-50 h-12 flex items-center border-b border-border/50 px-2 bg-background/95 backdrop-blur-sm">
            <SidebarToggleButton />
            <span className="ml-2 text-sm text-muted-foreground">Admin</span>
          </header>

        <div className="flex w-full">
          {/* Sidebar */}
          <Sidebar className="w-14 md:w-64 bg-card/95 backdrop-blur-sm border-r border-border/50">
            <SidebarHeader>
              <div className="relative mb-4 p-4 rounded-lg overflow-hidden">
                {/* Background Image */}
                <img
                  src="/lovable-uploads/df7a83c0-8d4c-488e-9ff0-aabf910cc91f.png"
                  alt="ASC Fechten Wien Logo"
                  className="absolute inset-0 w-full h-full object-cover opacity-20"
                  loading="lazy"
                />
                {/* Content */}
                <div className="relative z-10">
                  <h1 className="text-xl font-bold text-foreground">Admin Panel</h1>
                  <p className="text-sm text-muted-foreground">Akademischer Sportclub Fechten CMS</p>
                </div>
              </div>

              {/* User Info */}
              <Card className="bg-secondary/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-primary font-semibold">
                        {user?.email?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {user?.email}
                      </p>
                      <Badge variant="secondary" className="text-xs">
                        {userRole}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SidebarHeader>

            <SidebarContent>
              <SidebarGroup>
                <SidebarGroupLabel>Navigation</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {menuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;

                      // Verstecke bestimmte Bereiche für Moderatoren
                      if (userRole === 'moderator') {
                        const moderatorRestrictedPaths = ['/admin/users', '/admin/settings'];
                        if (moderatorRestrictedPaths.includes(item.path)) {
                          return null;
                        }
                      }

                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                            <Link to={item.path} className="justify-start gap-3">
                              <Icon className="h-4 w-4" />
                              {/* Label nur auf größeren Bildschirmen anzeigen */}
                              <span className="hidden md:inline">{item.label}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>

            <SidebarSeparator />

            <SidebarFooter>
              <div className="space-y-2">
                <Link to="/">
                  <Button variant="outline" className="w-full justify-start gap-3 h-10">
                    <Home className="h-4 w-4" />
                    <span>Zur Website</span>
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-10 text-destructive hover:text-destructive"
                  onClick={handleSignOut}
                >
                  <LogOut className="h-4 w-4" />
                  <span>Abmelden</span>
                </Button>
              </div>
            </SidebarFooter>
          </Sidebar>

          {/* Main Content */}
          <main className="flex-1 p-6">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
